import type { MeProfile, MeQuotas } from '@/api/services/me';
import { isUnlimitedSourceLimit, UNLIMITED_SOURCE_LIMIT } from '@/utils/sourceLimits';
import { formatPlanDisplayName } from '@/utils/planDisplayName';
import { isPlatformStaffRole } from '@/utils/teamRoles';

export type UserCapabilities = {
  unlimited: boolean;
  enforcePlanLimits: boolean;
  billingBypass: boolean;
  plan: string;
  maxSearches?: number;
  maxSubscriptions?: number;
  maxSources?: number;
  maxSourcesPerSearch?: number;
  accessibleSources?: number;
  canCreateSearch: boolean;
  isPlatformStaff: boolean;
};

export type SearchPlanValidationLimits = {
  maxSourcesPerSearch?: number;
  accessibleSources?: number;
  maxSearches?: number;
  searchCount?: number;
};

export function workspacePlanSlug(me: MeProfile | null | undefined): string | null {
  return me?.currentWorkspacePlan ?? me?.plan ?? null;
}

export function resolveCapabilities(
  me: MeProfile | null | undefined,
  quotas?: MeQuotas | null,
): UserCapabilities {
  const role = me?.role ?? 'subscriber';
  const billingBypass = Boolean(me?.billingBypass || quotas?.billingBypass || isPlatformStaffRole(role));
  const unlimited = Boolean(quotas?.unlimited ?? quotas?.capabilities?.unlimited ?? billingBypass);
  const enforcePlanLimits = quotas?.enforcePlanLimits ?? quotas?.capabilities?.enforcePlanLimits ?? !unlimited;

  if (unlimited || !enforcePlanLimits) {
    return {
      unlimited: true,
      enforcePlanLimits: false,
      billingBypass: true,
      plan: quotas?.plan ?? workspacePlanSlug(me) ?? 'free',
      canCreateSearch: me?.permissions.canCreateSearch ?? !me?.permissions.readOnly,
      isPlatformStaff: isPlatformStaffRole(role),
    };
  }

  const quotaBypass = Boolean(
    me?.permissions.canBypassNotificationQuota || me?.billingBypass || isPlatformStaffRole(role),
  );
  if (quotaBypass) {
    return {
      unlimited: false,
      enforcePlanLimits: true,
      billingBypass: true,
      plan: quotas?.plan ?? workspacePlanSlug(me) ?? 'free',
      maxSearches: quotas?.limits.maxSearches,
      maxSubscriptions: quotas?.limits.maxSubscriptions,
      maxSources: quotas?.limits.maxSources,
      maxSourcesPerSearch: quotas?.limits.maxSourcesPerSearch,
      accessibleSources: quotas?.usage?.accessibleSources,
      canCreateSearch: me?.permissions.canCreateSearch ?? false,
      isPlatformStaff: isPlatformStaffRole(role),
    };
  }

  return {
    unlimited: false,
    enforcePlanLimits: true,
    billingBypass: false,
    plan: quotas?.plan ?? workspacePlanSlug(me) ?? 'free',
    maxSearches: quotas?.limits.maxSearches,
    maxSubscriptions: quotas?.limits.maxSubscriptions,
    maxSources: quotas?.limits.maxSources,
    maxSourcesPerSearch: quotas?.limits.maxSourcesPerSearch,
    accessibleSources: quotas?.usage?.accessibleSources,
    canCreateSearch: me?.permissions.canCreateSearch ?? false,
    isPlatformStaff: false,
  };
}

export function searchPlanValidationLimits(
  capabilities: UserCapabilities,
  searchCount?: number,
): SearchPlanValidationLimits {
  if (!capabilities.enforcePlanLimits || capabilities.unlimited) {
    return {};
  }
  const maxSourcesPerSearch =
    capabilities.maxSourcesPerSearch != null &&
    isUnlimitedSourceLimit(capabilities.maxSourcesPerSearch)
      ? undefined
      : capabilities.maxSourcesPerSearch;
  const maxSearches =
    capabilities.maxSearches != null && capabilities.maxSearches >= UNLIMITED_SOURCE_LIMIT
      ? undefined
      : capabilities.maxSearches;
  return {
    maxSourcesPerSearch,
    accessibleSources: capabilities.accessibleSources,
    maxSearches,
    searchCount,
  };
}

export function platformRolePlanLabel(me: MeProfile | null | undefined): string {
  if (!me) return '';
  if (me.billingBypass || isPlatformStaffRole(me.role ?? '')) {
    return 'N/A';
  }
  const plan = workspacePlanSlug(me);
  if (!plan) return '—';
  return formatPlanDisplayName(plan);
}

export function shouldShowPlanLimitWarning(capabilities: UserCapabilities): boolean {
  return capabilities.enforcePlanLimits && !capabilities.unlimited;
}

export function quotaBypassBannerText(me: MeProfile | null | undefined): string | null {
  if (!me?.permissions.canBypassNotificationQuota && !me?.billingBypass && !isPlatformStaffRole(me?.role ?? '')) {
    return null;
  }
  return 'Quota bypass active via platform role.';
}
